import React, { useState, useEffect, useContext, ReactElement } from "react";
import {
  Paper,
  Grid,
  Divider,
  makeStyles,
  Theme,
  createStyles,
  Typography,
  TableRow,
  Tooltip,
  CardHeader,
  Card,
  CardContent,
  Box,
} from "@material-ui/core";
import UserContext from "../../../UserContext";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import StepHandler from "../../../models/shared/StepHandler";
import TrTable from "../../shared/controls/TrTable";
import TrSelect from "../../shared/controls/TrSelect";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import TrSelectData from "../../../models/shared/TrSelectData";
import PaymentRequestStudent from "../../../models/payment-request/PaymentRequestStudent";
import PaymentRequestSchoolCarrier from "../../../models/payment-request/PaymentRequestSchoolCarrier";
import PaymentRequestAuthorizedCarrier from "../../../models/payment-request/PaymentRequestAuthorizedCarrier";
import SchoolYear from "../../../models/school-year/SchoolYear";
import Month from "../../../models/school-year/Month";
import SchoolType from "../../../models/school-type/SchoolType";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";
import SchoolYearApiClient from "../../../api/SchoolYearApiClient";
import SchoolTypeApiClient from "../../../api/SchoolTypeApiClient";
import {
  hrDate,
  numberFormat,
  currencyFormat,
} from "../../../utilities/utilities";
import Currency from "../../../models/shared/Currency";

interface Props extends StepHandler {
  studentsState: PaymentRequestStudent[];
  schoolCarriersState: PaymentRequestSchoolCarrier[];
  authorizedCarriersState: PaymentRequestAuthorizedCarrier[];
  schoolTypeId: number;
  schoolTypes: SchoolType[];
  schoolYearId: number;
  currency: Currency;
  schoolYears: SchoolYear[];
  monthDate?: Date | null;
  disableMonthPick?: boolean;
  updateStudents: (students: PaymentRequestStudent[]) => void;
  updateSchoolCarriers: (schoolCarriers: PaymentRequestSchoolCarrier[]) => void;
  updateAuthorizedCarriers: (
    authorizedCarriers: PaymentRequestAuthorizedCarrier[]
  ) => void;
  updateSchoolType: (schoolTypeId: number) => void;
  updateSchoolTypes: (schoolType: SchoolType[]) => void;
  updateSchoolYear: (schoolYearId: number) => void;
  updateSchoolYears: (schoolYears: SchoolYear[]) => void;
  updateMonth: (monthDate?: Date) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    divider: {
      width: "100%",
    },
  })
);

export default function PaymentRequestSelectStep({
  studentsState,
  schoolCarriersState,
  authorizedCarriersState,
  schoolTypeId,
  schoolTypes,
  schoolYearId,
  currency,
  schoolYears,
  monthDate,
  disableMonthPick = false,
  updateStudents,
  updateSchoolCarriers,
  updateAuthorizedCarriers,
  updateSchoolType,
  updateSchoolTypes,
  updateSchoolYear,
  updateSchoolYears,
  updateMonth,
  buttons,
  onNext,
  goToNextStep,
  onStepperSave,
}: Props) {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [students, setStudents] = useState<PaymentRequestStudent[]>([]);
  const [schoolCarriers, setSchoolCarriers] = useState<
    PaymentRequestSchoolCarrier[]
  >([]);
  const [authorizedCarriers, setAuthorizedCarriers] = useState<
    PaymentRequestAuthorizedCarrier[]
  >([]);

  const [refreshTables, setRefreshTables] = React.useState<boolean>(false);

  useEffect(() => {
    async function getSchoolTypes() {
      const apiClient = new SchoolTypeApiClient();
      const schoolTypes = await apiClient.getSchoolTypes();

      updateSchoolTypes(schoolTypes);
    }

    async function getSchoolYears() {
      const apiClient = new SchoolYearApiClient();
      const dateFrom = new Date(2020, 0, 1);
      const schoolYears = await apiClient.getSchoolYearsFromToCurrentMonthForYear(
        dateFrom,
        1
      );
      const lastSchoolYear = schoolYears[0].id || 0;

      updateSchoolYears(schoolYears);
      updateSchoolYear(lastSchoolYear);
    }

    getSchoolTypes();

    if (schoolYears == null || schoolYears.length == 0) {
      getSchoolYears();
    }
  }, []);

  // useEffect(() => {
  //   setLoading(true);
  //   async function getStudents() {
  //     const apiClient = new PaymentRequestApiClient();
  //     const students = await apiClient.getStudentsForNewPaymentRequests(
  //       schoolTypeId,
  //       monthDate!
  //     );

  //     setStudents(students);
  //     setRefreshTables(false);
  //     setLoading(false);
  //   }

  //   async function getSchoolCarriers() {
  //     setLoading(true);
  //     const apiClient = new PaymentRequestApiClient();
  //     const schoolCarriers = await apiClient.getSchoolCarriersForNewPaymentRequests(
  //       schoolYearId,
  //       monthDate!
  //     );

  //     setSchoolCarriers(schoolCarriers);
  //     setRefreshTables(false);
  //     setLoading(false);
  //   }

  //   async function getAuthorizedCarriers() {
  //     setLoading(true);
  //     const apiClient = new PaymentRequestApiClient();
  //     const authorizedCarriers = await apiClient.getAuthorizedCarriersForNewPaymentRequests(
  //       schoolYearId,
  //       monthDate!
  //     );

  //     setAuthorizedCarriers(authorizedCarriers);
  //     setRefreshTables(false);
  //     setLoading(false);
  //   }

  //   if (refreshTables) {
  //     setStudents([]);
  //     getStudents();

  //     setSchoolCarriers([]);
  //     getSchoolCarriers();

  //     setAuthorizedCarriers([]);
  //     getAuthorizedCarriers();
  //   }
  // }, [refreshTables]);

  useEffect(() => {
    setLoading(true);
    async function getAllPaymentRequestData() {
      const apiClient = new PaymentRequestApiClient();
      const data = await apiClient.getAllPaymentRequestData(
        schoolTypeId,
        schoolYearId,
        monthDate!
      );
      setStudents(data.students);
      setSchoolCarriers(data.schoolCarriers);
      setAuthorizedCarriers(data.authorizedCarriers);
      setRefreshTables(false);
      setLoading(false);
    }
    if (refreshTables) {
      getAllPaymentRequestData();
    }
  }, [refreshTables, schoolTypeId, schoolYearId, monthDate]);
  

  useEffect(() => {
    if (studentsState.length > 0) {
      setStudents(studentsState);
    }
    if (schoolCarriersState.length > 0) {
      setSchoolCarriers(schoolCarriersState);
    }
    if (authorizedCarriersState.length > 0) {
      setAuthorizedCarriers(authorizedCarriersState);
    }
  }, [studentsState, schoolCarriersState, authorizedCarriersState]);

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
  } = useSnackbar();

  function getSchoolTypes() {
    return {
      valueProp: "id",
      textProp: "name",
      data: schoolTypes,
    } as TrSelectData<SchoolType>;
  }

  function getSchoolYearItems() {
    const years: TrSelectData<SchoolYear> = {
      valueProp: "id",
      textProp: "name",
      data: schoolYears,
    };

    return years;
  }

  function getMonthItems() {
    const selectedYear = schoolYears.find((s) => s.id == schoolYearId);

    let selectedMonths = new Array<Month>();
    if (selectedYear) {
      selectedYear.months.forEach((month) => {
        selectedMonths.push({
          date: month.date,
          name:
            month.name.charAt(0).toUpperCase() +
            month.name.slice(1) +
            " - " +
            new Date(month.date).getFullYear(),
        });
      });
    }

    const months: TrSelectData<Month> = {
      valueProp: "date",
      textProp: "name",
      data: selectedMonths,
    };
    return months;
  }

  const handleNextStep = () => {
    if (
      students &&
      students.filter((s) => s.isSelected).length === 0 &&
      schoolCarriers &&
      schoolCarriers.filter((sc) => sc.isSelected).length === 0 &&
      authorizedCarriers &&
      authorizedCarriers.filter((ac) => ac.isSelected).length === 0
    ) {
      openSnackbar();
    } else {
      updateStudents(students);
      updateSchoolCarriers(schoolCarriers);
      updateAuthorizedCarriers(authorizedCarriers);
      goToNextStep && goToNextStep();
    }
  };

  const handleSchoolTypeChange = (
    e: React.ChangeEvent<{ name?: string | undefined; value: any }>
  ) => {
    const { value } = e.target;
    updateSchoolType(value);
    if (monthDate) {
      setRefreshTables(true);
    }
  };

  const handleSchoolYearChange = (
    e: React.ChangeEvent<{ name?: string | undefined; value: any }>
  ) => {
    const { value } = e.target;
    updateSchoolYear(value);
    setStudents([]);
    setSchoolCarriers([]);
    setAuthorizedCarriers([]);
    updateMonth(undefined);
  };

  const handleMonthChange = (
    e: React.ChangeEvent<{ name?: string | undefined; value: any }>
  ) => {
    const { value } = e.target;
    updateMonth(value);
    setRefreshTables(true);
  };

  const handleStudentSelectChange = (studentIds: string[]) => {
    if (students != null) {
      let studentsProp = [...students];
      studentsProp.forEach((s) => {
        s.isSelected = studentIds.includes(s.id.toString());
      });

      setStudents(studentsProp);
    }
  };

  const handleSchoolCarrierSelectChange = (schoolCarrierIds: string[]) => {
    if (schoolCarriers != null) {
      let schoolCarriersProp = [...schoolCarriers];
      schoolCarriersProp.forEach((s) => {
        s.isSelected = schoolCarrierIds.includes(s.id.toString());
      });

      setSchoolCarriers(schoolCarriersProp);
    }
  };

  const handleAuthorizedCarrierSelectChange = (
    authorizedCarrierIds: string[]
  ) => {
    if (authorizedCarriers != null) {
      let authorizedCarriersProp = [...authorizedCarriers];
      authorizedCarriersProp.forEach((s) => {
        s.isSelected = authorizedCarrierIds.includes(s.id.toString());
      });

      setAuthorizedCarriers(authorizedCarriersProp);
    }
  };

  const studentsTableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestStudent
  >[] = [
      {
        id: "student",
        label: "Učenik",
        dataName: "lastName",
        dataFunc: (d: PaymentRequestStudent) => (
          <>
            <Typography variant="body2">
              {d.lastName + " " + d.firstName}
            </Typography>
            <Typography variant="body2">{hrDate(d.birthDate)}</Typography>
          </>
        ),
      },
      { id: "oib", label: "OIB", dataName: "oib" },
      {
        id: "schoolClass",
        label: "Razred",
        dataName: "schoolClass",
        dataFunc: (d: PaymentRequestStudent) => (
          <>
            <Typography variant="body2">{d.programName}</Typography>
            <Typography variant="body2">
              {d.schoolClass + " - " + d.sectionMark}
            </Typography>
          </>
        ),
      },
      {
        id: "vrstaOdjeljenja",
        label: "Vrsta odjeljenja",
        dataFunc: (d: PaymentRequestStudent) => (
          <Typography variant="body2">{d.sectionTypeName}</Typography>
        ),
      },
      { id: "remark", label: "Napomena", dataName: "remark" },
    ];

  const studentsTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: students,
  };

  const studentsPreSelectedIds: string[] =
    students != null
      ? students.filter((s) => s.isSelected).map((s) => s.id.toString())
      : [];

  const studentsDisabledIds: string[] =
    students != null
      ? students.filter((s) => !s.isEnabled).map((s) => s.id.toString())
      : [];

  const handleStudentsBodyRowCreated = (
    tableRow: ReactElement,
    rowData: any
  ) => {
    return rowData.isEnabled ? (
      <TableRow key={rowData.id} {...tableRow.props} />
    ) : (
      <Tooltip key={rowData.id} title="Za učenika je već kreiran zahtjev." placement="top">
        <TableRow {...tableRow.props} />
      </Tooltip>
    );
  };

  const schoolCarriersTableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestSchoolCarrier
  >[] = [
      {
        id: "vehicleTypeName",
        label: "Vrsta vozila",
        dataName: "vehicleTypeName",
      },
      {
        id: "registrationPlate",
        label: "Registracijske oznake",
        dataName: "registrationPlate",
      },
      {
        id: "dailyMileageTotal",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileageTotal",
        dataFunc: (d: PaymentRequestSchoolCarrier) => (
          <>
            <Typography variant="body2">
              {numberFormat(d.dailyMileageTotal, "km")}
            </Typography>
          </>
        ),
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika",
        dataName: "numberOfStudentsTotal",
      },
    ];

  const schoolCarriersTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: schoolCarriers,
  };

  const schoolCarriersPreSelectedIds: string[] =
    schoolCarriers != null
      ? schoolCarriers.filter((s) => s.isSelected).map((s) => s.id.toString())
      : [];

  const schoolCarriersDisabledIds: string[] =
    schoolCarriers != null
      ? schoolCarriers.filter((s) => !s.isEnabled).map((s) => s.id.toString())
      : [];

  const handleSchoolCarriersBodyRowCreated = (
    tableRow: ReactElement,
    rowData: any
  ) => {
    const title =
      rowData.numberOfStudentsTotal > 0
        ? "Za školski prijevoz je već kreiran zahtjev."
        : "Školskim prijevozom se ne vozi nijedan učenik.";

    return rowData.isEnabled ? (
      <TableRow key={rowData.id} {...tableRow.props} />
    ) : (
      <Tooltip key={rowData.id} title={title} placement="top">
        <TableRow {...tableRow.props} />
      </Tooltip>
    );
  };

  const authorizedCarriersTableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestAuthorizedCarrier
  >[] = [
      {
        id: "name",
        label: "Naziv prijevoznika",
        dataName: "name",
      },
      {
        id: "dailyMileage",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileage",
        dataFunc: (d: PaymentRequestAuthorizedCarrier) => (
          <>
            <Typography variant="body2">
              {numberFormat(d.dailyMileage, "km")}
            </Typography>
          </>
        ),
      },
      {
        id: "dailyPrice",
        label: "Cijena po danu",
        dataName: "dailyPrice",
        dataFunc: (d: PaymentRequestAuthorizedCarrier) => (
          <>
            <Typography variant="body2">
              {currencyFormat(d.dailyPrice, currency)}
            </Typography>
          </>
        ),
      },
      {
        id: "numberOfStudents",
        label: "Broj učenika",
        dataName: "numberOfStudents",
      },
    ];

  const authorizedCarriersTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: authorizedCarriers,
  };

  const authorizedCarriersPreSelectedIds: string[] =
    authorizedCarriers != null
      ? authorizedCarriers
        .filter((a) => a.isSelected)
        .map((s) => s.id.toString())
      : [];

  const authorizedCarriersDisabledIds: string[] =
    authorizedCarriers != null
      ? authorizedCarriers
        .filter((a) => !a.isEnabled)
        .map((s) => s.id.toString())
      : [];

  const handleAuthorizedCarriersBodyRowCreated = (
    tableRow: ReactElement,
    rowData: any
  ) => {
    const title =
      rowData.numberOfStudentsTotal > 0
        ? "Za ovlaštenog prijevoznika je već kreiran zahtjev."
        : "Ovlaštenim prijevoznikom se ne vozi nijedan učenik.";

    return rowData.isEnabled ? (
      <TableRow key={rowData.id} {...tableRow.props} />
    ) : (
      <Tooltip key={rowData.id} title={title} placement="top">
        <TableRow {...tableRow.props} />
      </Tooltip>
    );
  };

  const handleUpdateState = () => {
    updateStudents(students);
    updateSchoolCarriers(schoolCarriers);
    updateAuthorizedCarriers(authorizedCarriers);
  };

  onNext && onNext(handleNextStep);

  onStepperSave && onStepperSave(handleUpdateState);

  return (
    <>
      <Box bgcolor="background.paper">
        <Paper>
          <Grid container spacing={4} className="noMarginPadding">
            {user.institutionTypes.length > 1 ? (
              <Grid item md={3}>
                <TrSelect
                  required
                  disabled={schoolTypes.length == 0 || disableMonthPick}
                  value={schoolTypeId}
                  id="SchoolTypeSelect"
                  className="selectFormControl"
                  label="Tip škole"
                  onChange={handleSchoolTypeChange}
                  selectData={getSchoolTypes()}
                />
              </Grid>
            ) : null}
            <Grid item md={3}>
              <TrSelect
                required
                disabled={schoolYears.length == 0 || disableMonthPick}
                value={schoolYearId}
                id="SchoolYearSelect"
                className="selectFormControl"
                label="Školska godina"
                onChange={handleSchoolYearChange}
                selectData={getSchoolYearItems()}
              />
            </Grid>
            <Grid item md={3}>
              <TrSelect
                required
                disabled={schoolYearId == 0 || disableMonthPick}
                value={monthDate}
                id="MonthSelect"
                className="selectFormControl"
                label="Mjesec za isplatu"
                onChange={handleMonthChange}
                selectData={getMonthItems()}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {schoolCarriers.length > 0 ? (
        <Box display="flex">
          <Box mt={1} flexGrow={1}>
            <Card>
              <CardHeader
                title="Školski prijevozi"
                titleTypographyProps={{ variant: "h6" }}
              />
              <Divider />
              <CardContent>
                <TrTable
                  hover
                  enableSelect
                  enableAllSelect
                  headMetadata={schoolCarriersTableHeadMetadata}
                  bodyData={schoolCarriersTableBodyData}
                  preSelectedIds={schoolCarriersPreSelectedIds}
                  disabledIds={schoolCarriersDisabledIds}
                  onSelectChange={(schoolCarrierIds: string[]) =>
                    handleSchoolCarrierSelectChange(schoolCarrierIds)
                  }
                  onBodyRowCreated={handleSchoolCarriersBodyRowCreated}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : null}

      {authorizedCarriers.length > 0 ? (
        <Box display="flex">
          <Box mt={1} flexGrow={1}>
            <Card>
              <CardHeader
                title="Ovlašteni prijevoznici"
                titleTypographyProps={{ variant: "h6" }}
              />
              <Divider />
              <CardContent>
                <TrTable
                  hover
                  enableSelect
                  enableAllSelect
                  headMetadata={authorizedCarriersTableHeadMetadata}
                  bodyData={authorizedCarriersTableBodyData}
                  preSelectedIds={authorizedCarriersPreSelectedIds}
                  disabledIds={authorizedCarriersDisabledIds}
                  onSelectChange={(authorizedCarrierIds: string[]) =>
                    handleAuthorizedCarrierSelectChange(authorizedCarrierIds)
                  }
                  onBodyRowCreated={handleAuthorizedCarriersBodyRowCreated}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : null}

      {students.length > 0 ? (
        <Box display="flex">
          <Box mt={1} flexGrow={1}>
            <Card>
              <CardHeader
                title="Učenici"
                titleTypographyProps={{ variant: "h6" }}
              />
              <Divider />
              <CardContent>
                <TrTable
                  hover
                  enableSelect
                  enableAllSelect
                  headMetadata={studentsTableHeadMetadata}
                  bodyData={studentsTableBodyData}
                  preSelectedIds={studentsPreSelectedIds}
                  disabledIds={studentsDisabledIds}
                  onSelectChange={(studentIds: string[]) =>
                    handleStudentSelectChange(studentIds)
                  }
                  onBodyRowCreated={handleStudentsBodyRowCreated}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : null}

      {!loading &&
        schoolTypeId &&
        schoolYearId &&
        monthDate &&
        schoolCarriers.length === 0 &&
        authorizedCarriers.length === 0 &&
        students.length === 0 ? (
        <Typography variant="h6" align="center" style={{ marginTop: "20px" }}>
          Ne postoje zapisi za odabrane filtere.
        </Typography>
      ) : null}

      {buttons}
      <TrSnackbar
        variant={"error"}
        isOpened={isSnackbarOpened}
        message={
          "Ne možete prijeći na sljedeći korak dok ne odaberete barem jednu stavku."
        }
        onClose={closeSnackbar}
      />
    </>
  );


}
